import ApiErrorBanner from '@/components/ApiErrorBanner';
import type { StudyReportSubType } from '@/components/Studies/StudyExports/ExportsList/Exports.model';
import Button from '@/components/UI/Button';
import Checkbox from '@/components/UI/FormElements/Checkbox/Checkbox';
import { errorToast, infoToast } from '@/helpers';
import { _isEmpty, _notNil } from '@/littledash';
import type { Study } from '@/model/Study.model';
import { ApiService, ResponseType } from '@/support/ApiService';
import { EXPORT_LIMIT_TOAST_MESSAGE, isExportLimitError } from '@/support/asyncExport';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useRef, useState, type FC } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

type ExportFormValues = Record<StudyReportSubType, boolean>;

interface ExportTypeOption {
  name: StudyReportSubType;
  label: string;
  subLabel: string;
  disabled?: boolean;
}

interface ExportV2Props {
  study: Study;
}

const SUB_TYPES: Array<StudyReportSubType> = ['study_data', 'groups', 'prism', 'dosing'];

const DEFAULT_FORM_VALUES: ExportFormValues = {
  study_data: false,
  groups: false,
  prism: false,
  dosing: false,
};

const buildExportTypes = (study: Study): Array<ExportTypeOption> => [
  {
    name: 'study_data',
    label: 'Raw study data',
    subLabel: 'All animals and their measurements',
  },
  {
    name: 'groups',
    label: 'Study group report',
    subLabel: 'Includes relative values',
    disabled: _isEmpty(study.study_groups),
  },
  {
    name: 'prism',
    label: 'Prism report',
    subLabel: 'Including relative values in PZFX format',
    disabled: _isEmpty(study.study_groups),
  },
  {
    name: 'dosing',
    label: 'Study dosing',
    subLabel: 'Includes study groups and treatments',
  },
];

const selectedTypesFromForm = (values: ExportFormValues): Array<StudyReportSubType> =>
  SUB_TYPES.reduce<Array<StudyReportSubType>>((acc, name) => {
    if (values[name]) {
      acc.push(name);
    }
    return acc;
  }, []);

/**
 * Rendered under the "New Export" tab of the tabbed StudyExports page. Posts a
 * single async `study_report` create request with the user's selected sub-types
 * and fire-and-forgets the legacy PHP generator. Audit-log export is a separate
 * type with its own create call below. Mounted only when the `async_exports`
 * feature flag is on (gated by the parent route).
 */
const ExportV2: FC<ExportV2Props> = ({ study }) => {
  const [studyReportLoading, setStudyReportLoading] = useState(false);
  const [studyReportError, setStudyReportError] = useState<unknown>(null);
  const [auditLogGenerating, setAuditLogGenerating] = useState(false);
  const [auditLogError, setAuditLogError] = useState<unknown>(null);

  // Synchronous guards against spam clicks: state updates flip `disabled` on the
  // next render, but rapid clicks (and react-hook-form's async validation pass
  // before our submit handler) can both squeak through that gap and fire two
  // POSTs. Refs flip immediately, closing the race.
  const studyReportInFlightRef = useRef(false);
  const auditLogInFlightRef = useRef(false);

  const { invoke: createAuditLogExport } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/exports/audit-logs',
    options: { invokeOnInit: false, onError: { toast: false, throw: false, slug: 'audit-log-async-export' } },
  });

  const exportTypes = buildExportTypes(study);

  const { handleSubmit, control } = useForm<ExportFormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange',
  });

  const watchedValues = useWatch({ control }) as Partial<ExportFormValues>;
  const noTypesSelected = SUB_TYPES.every((name) => watchedValues[name] !== true);

  const submitExport = async (values: ExportFormValues): Promise<void> => {
    if (studyReportInFlightRef.current) {
      return;
    }
    const subTypes = selectedTypesFromForm(values);
    if (_isEmpty(subTypes)) {
      return;
    }

    studyReportInFlightRef.current = true;
    setStudyReportLoading(true);
    setStudyReportError(null);

    try {
      const response = await ApiService.call({
        endpoint: 'POST /api/v1/studies/{studyApiId}/exports/study-report',
        path: { studyApiId: study.api_id },
        body: { sub_types: subTypes },
        options: { onError: { toast: false, throw: false, slug: 'study-report-async-export' } },
      });

      if (response.type !== ResponseType.Success) {
        if (isExportLimitError(response.error)) {
          errorToast(EXPORT_LIMIT_TOAST_MESSAGE);
        } else {
          setStudyReportError(response.error);
        }
        return;
      }

      infoToast('Generating export. You will receive an email when complete.');
    } finally {
      studyReportInFlightRef.current = false;
      setStudyReportLoading(false);
    }
  };

  const onGenerateClick = handleSubmit(submitExport);

  const generateAuditLogReport = async (): Promise<void> => {
    if (auditLogInFlightRef.current) {
      return;
    }
    auditLogInFlightRef.current = true;
    setAuditLogGenerating(true);
    setAuditLogError(null);

    try {
      const response = await createAuditLogExport({ path: { studyApiId: study.api_id } });

      if (response.type !== ResponseType.Success) {
        if (isExportLimitError(response.error)) {
          errorToast(EXPORT_LIMIT_TOAST_MESSAGE);
        } else {
          setAuditLogError(response.error);
        }
        return;
      }

      infoToast('Generating export. You will receive an email when complete.');
    } finally {
      auditLogInFlightRef.current = false;
      setAuditLogGenerating(false);
    }
  };

  const studyReportDisabled = studyReportLoading || noTypesSelected;

  return (
    <div data-testid="study-exports__generate">
      <div className="ui-card mt3 ph3 pb3 mw6">
        {_notNil(studyReportError) && <ApiErrorBanner error={studyReportError} />}
        <form>
          <div>
            {exportTypes.map((type) => (
              <div className="mv3 pb3 bb" key={type.name}>
                <Controller
                  control={control}
                  name={type.name}
                  render={({ field: { onChange, value, name } }) => (
                    <Checkbox
                      label={type.label}
                      sublabel={type.subLabel}
                      id={type.name}
                      disabled={type.disabled === true}
                      name={name}
                      checked={value}
                      onChange={(event) => onChange(event.target.checked)}
                    />
                  )}
                />
              </div>
            ))}
          </div>
          <Button disabled={studyReportDisabled} onClick={onGenerateClick} testKey="study-report-generate">
            Generate export
          </Button>
        </form>
      </div>

      <div
        className="ui-card pa3 mt4 mw6"
        data-test-component="ExportV2"
        data-test-element="audit-log-section"
        data-testid="export-audit-log-section"
      >
        <h3 className="f5 normal pb2">Generate audit log</h3>
        <p className="pb3 bb f6 ma0 dark-gray">
          A historical log of samples, measurements, observations etc. The export will appear in the Exports tab when
          ready.
        </p>
        {_notNil(auditLogError) && <ApiErrorBanner error={auditLogError} />}
        <form className="pt3">
          <Button
            className="plain"
            disabled={auditLogGenerating}
            onClick={generateAuditLogReport}
            testKey="audit-log-generate"
          >
            Generate audit log
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ExportV2;
